/**
 * Decimal-safe money formatting & quantisation.
 *
 * Why this exists:
 *   Native `Number.prototype.toFixed(2)` uses IEEE-754 double-precision
 *   arithmetic + banker's-rounding edge cases. Values like 199.995 or
 *   99.995 — which can arise from float accumulation (e.g. running totals
 *   on the Dashboard) or from GST round-trips — render as "199.99" and
 *   "99.99" instead of the user-expected "200.00" / "100.00".
 *
 *   This module quantises every value through Decimal.js with explicit
 *   ROUND_HALF_UP so the display always matches the stored intent. All
 *   monetary formatters in the app should funnel through here.
 */
import Decimal from "decimal.js";

// 28-digit precision (Decimal.js default) is overkill for cents but
// matches the configuration used at the entry-edit boundary.
Decimal.set({ precision: 28, rounding: Decimal.ROUND_HALF_UP });

/**
 * Quantise a number/string to a 2dp Number. Returns 0 for invalid input
 * so callers can safely use the result in further arithmetic without
 * NaN propagation.
 *
 *   q2(199.995) === 200
 *   q2("99.995") === 100
 *   q2("") === 0
 *   q2(null) === 0
 */
export function q2(n) {
  if (n === null || n === undefined || n === "") return 0;
  try {
    const d = new Decimal(n);
    if (!d.isFinite()) return 0;
    return Number(d.toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toFixed(2));
  } catch {
    return 0;
  }
}

/**
 * Format a value as a fixed 2dp string. Always returns "X.YZ" (no
 * grouping). Use this when you want the raw number — for currency
 * symbols use fmtMoney.
 *
 *   fmt2(199.995) === "200.00"
 *   fmt2("100")   === "100.00"
 */
export function fmt2(n) {
  if (n === null || n === undefined || n === "") return "0.00";
  try {
    const d = new Decimal(n);
    if (!d.isFinite()) return "0.00";
    return d.toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toFixed(2);
  } catch {
    return "0.00";
  }
}

/**
 * Format as a money string. Default currency NZD (matches the app's
 * existing default), but accepts a currency code for future i18n.
 *
 *   fmtMoney(199.995)        === "$200.00 NZD"
 *   fmtMoney(0)              === "$0.00 NZD"
 *   fmtMoney(1234.5, "USD")  === "$1234.50 USD"
 *   fmtMoney(99.995, null, { symbol: "$", suffix: false }) === "$100.00"
 */
export function fmtMoney(n, code = "NZD", { symbol = "$", suffix = true } = {}) {
  const v = fmt2(n);
  return suffix && code ? `${symbol}${v} ${code}` : `${symbol}${v}`;
}

/**
 * Sum an iterable of numeric values using Decimal so accumulation never
 * drifts. Returns a 2dp Number ready for display.
 *
 *   sumQ2([100, 99.995, 0.01]) === 200.01
 */
export function sumQ2(values) {
  let total = new Decimal(0);
  for (const v of values || []) {
    if (v === null || v === undefined || v === "") continue;
    try {
      const d = new Decimal(v);
      if (d.isFinite()) total = total.plus(d);
    } catch {
      /* skip non-numeric */
    }
  }
  return Number(total.toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toFixed(2));
}
